import { z } from 'zod'

/**
 * Géométrie 3D des rangements (pièces / meubles), indépendante de Three.js.
 * Unités : mètres ; Y vertical ; rotation autour de Y en degrés.
 * Origine locale : coin sud-ouest du lieu (place), au niveau du sol de l'étage.
 *
 * Les rangements internes (tiroirs, cases) n'ont en général pas de géométrie :
 * on clique le meuble parent, puis on navigue dans l'arbre / la grille.
 */

export const GEOMETRY_VERSION = 1 as const

export const FURNITURE_MODELS = [
  'box',
  'sofa',
  'table',
  'shelf',
  'cabinet',
  'wardrobe',
  'drawers',
  'bed',
  'counter',
  'fridge',
  'vanity',
  'plant',
] as const
export type FurnitureModel = (typeof FURNITURE_MODELS)[number]

export const FLOOR_STYLES = ['wood', 'tile', 'plain'] as const
export type FloorStyle = (typeof FLOOR_STYLES)[number]

export const GeometryBoxSchema = z.object({
  version: z.literal(GEOMETRY_VERSION),
  kind: z.literal('box'),
  /** Position du coin min (sol) en mètres. */
  x: z.number().finite(),
  y: z.number().finite().default(0),
  z: z.number().finite(),
  width: z.number().positive().max(100),
  height: z.number().positive().max(20),
  depth: z.number().positive().max(100),
  /** Rotation autour de Y, degrés. */
  rotationY: z.number().finite().default(0),
  /** Couleur `#rrggbb` optionnelle. */
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  /** Meuble : modèle dessiné (sinon deviné d'après le nom, voir `furnitureModelFor`). */
  model: z.enum(FURNITURE_MODELS).optional(),
  /** Pièce : sol (sinon deviné d'après le nom, voir `floorStyleFor`). */
  floorStyle: z.enum(FLOOR_STYLES).optional(),
})
export type GeometryBox = z.infer<typeof GeometryBoxSchema>

export const PlanPointSchema = z.object({ x: z.number().finite(), z: z.number().finite() })
export type PlanPoint = z.infer<typeof PlanPointSchema>

/**
 * Pièce de forme libre (décision §9, option C) : contour au sol, points dans l'ordre (sens indifférent), dans le
 * repère du lieu. Réservé aux pièces ; les meubles restent des boîtes.
 */
export const GeometryPolygonSchema = z.object({
  version: z.literal(GEOMETRY_VERSION),
  kind: z.literal('polygon'),
  points: z.array(PlanPointSchema).min(3).max(64),
  y: z.number().finite().default(0),
  height: z.number().positive().max(20),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  floorStyle: z.enum(FLOOR_STYLES).optional(),
})
export type GeometryPolygon = z.infer<typeof GeometryPolygonSchema>

/** Formats acceptés : boîte (pièces, meubles) ou polygone (pièces). Extensible (glTF…) via `kind`. */
export const LocationGeometrySchema = z.discriminatedUnion('kind', [GeometryBoxSchema, GeometryPolygonSchema])
export type LocationGeometry = z.infer<typeof LocationGeometrySchema>

export function parseLocationGeometry(raw: unknown): LocationGeometry | null {
  const parsed = LocationGeometrySchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}

export const isBoxGeometry = (g: LocationGeometry): g is GeometryBox => g.kind === 'box'

/** Aire signée d'un contour (positive dans un sens, négative dans l'autre). */
export function polygonSignedArea(points: PlanPoint[]): number {
  let a = 0
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!
    const q = points[(i + 1) % points.length]!
    a += p.x * q.z - q.x * p.z
  }
  return a / 2
}

/** Centre de gravité d'un contour (milieu de l'emprise s'il est dégénéré). */
export function polygonCentroid(points: PlanPoint[]): PlanPoint {
  const a = polygonSignedArea(points)
  if (Math.abs(a) < 1e-9) {
    const b = pointsBounds(points)
    return { x: (b.minX + b.maxX) / 2, z: (b.minZ + b.maxZ) / 2 }
  }
  let cx = 0
  let cz = 0
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!
    const q = points[(i + 1) % points.length]!
    const f = p.x * q.z - q.x * p.z
    cx += (p.x + q.x) * f
    cz += (p.z + q.z) * f
  }
  return { x: cx / (6 * a), z: cz / (6 * a) }
}

/** Le point est-il à l'intérieur du contour (règle pair-impair) ? */
export function pointInPolygon(p: PlanPoint, points: PlanPoint[]): boolean {
  let inside = false
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i]!
    const b = points[j]!
    if (a.z > p.z !== b.z > p.z && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x) inside = !inside
  }
  return inside
}

export type Bounds = { minX: number; maxX: number; minZ: number; maxZ: number }

export function pointsBounds(points: PlanPoint[]): Bounds {
  return {
    minX: Math.min(...points.map((p) => p.x)),
    maxX: Math.max(...points.map((p) => p.x)),
    minZ: Math.min(...points.map((p) => p.z)),
    maxZ: Math.max(...points.map((p) => p.z)),
  }
}

/**
 * Centre d'une forme. Boîte : la rotation `rotationY` se fait **autour de ce centre** (comme au rendu Three.js) :
 * `x`, `z` sont le coin min de la boîte avant rotation, la rotation ne déplace donc pas le centre.
 * Polygone : centre de gravité du contour.
 */
export function geometryCenter(g: LocationGeometry): { x: number; y: number; z: number } {
  if (g.kind === 'polygon') {
    const c = polygonCentroid(g.points)
    return { x: c.x, y: (g.y ?? 0) + g.height / 2, z: c.z }
  }
  return { x: g.x + g.width / 2, y: (g.y ?? 0) + g.height / 2, z: g.z + g.depth / 2 }
}

/** Contour au sol, dans le repère du lieu : coins de la boîte (rotation comprise) ou points du polygone. */
export function geometryOutline(g: LocationGeometry): PlanPoint[] {
  if (g.kind === 'polygon') return g.points
  return [
    boxLocalToWorld(g, -g.width / 2, -g.depth / 2),
    boxLocalToWorld(g, g.width / 2, -g.depth / 2),
    boxLocalToWorld(g, g.width / 2, g.depth / 2),
    boxLocalToWorld(g, -g.width / 2, g.depth / 2),
  ].map((p) => ({ x: clean(p.x), z: clean(p.z) }))
}

/** Emprise au sol (rectangle droit englobant). */
export function geometryBounds(g: LocationGeometry): Bounds {
  return pointsBounds(geometryOutline(g))
}

/** Pièce rectangulaire → même pièce en forme libre (quatre coins), pour modifier son contour. */
export function boxToPolygon(g: LocationGeometry): GeometryPolygon {
  if (g.kind === 'polygon') return g
  return {
    version: GEOMETRY_VERSION,
    kind: 'polygon',
    points: geometryOutline(g),
    y: g.y ?? 0,
    height: g.height,
    ...(g.color ? { color: g.color } : {}),
    ...(g.floorStyle ? { floorStyle: g.floorStyle } : {}),
  }
}

const round = (n: number, step: number) => Math.round(n / step) * step
const clean = (n: number) => Math.round(n * 1e6) / 1e6
const DEG = Math.PI / 180

/** Point du repère local de la boîte (origine au centre, sans rotation) → repère du lieu (convention Three.js `rotation.y`). */
export function boxLocalToWorld(g: GeometryBox, lx: number, lz: number): { x: number; z: number } {
  const c = geometryCenter(g)
  const a = (g.rotationY ?? 0) * DEG
  return { x: c.x + lx * Math.cos(a) + lz * Math.sin(a), z: c.z - lx * Math.sin(a) + lz * Math.cos(a) }
}

/** Inverse de `boxLocalToWorld`. */
export function boxWorldToLocal(g: GeometryBox, wx: number, wz: number): { x: number; z: number } {
  const c = geometryCenter(g)
  const a = (g.rotationY ?? 0) * DEG
  const dx = wx - c.x
  const dz = wz - c.z
  return { x: dx * Math.cos(a) - dz * Math.sin(a), z: dx * Math.sin(a) + dz * Math.cos(a) }
}

/** Distance entre la poignée de rotation et le bord avant (−Z local) de la boîte, en mètres. */
export const ROTATE_HANDLE_GAP = 0.4

/**
 * Redimensionne la boîte en tirant son coin (+X, +Z local) vers `pointer` (repère du lieu) ; le coin opposé
 * reste fixe. Largeur / profondeur arrondies à `step` (magnétisme) et au moins `min`.
 */
export function resizeBoxFromCorner(
  g: GeometryBox,
  pointer: { x: number; z: number },
  opts: { step?: number; min?: number } = {},
): GeometryBox {
  const step = opts.step ?? 0.05
  const min = opts.min ?? 0.1
  const local = boxWorldToLocal(g, pointer.x, pointer.z)
  const width = Math.min(100, Math.max(min, round(local.x + g.width / 2, step)))
  const depth = Math.min(100, Math.max(min, round(local.z + g.depth / 2, step)))
  // Nouveau centre : milieu entre le coin opposé (fixe) et le nouveau coin, en local puis dans le lieu.
  const center = boxLocalToWorld(g, -g.width / 2 + width / 2, -g.depth / 2 + depth / 2)
  return { ...g, width: clean(width), depth: clean(depth), x: clean(center.x - width / 2), z: clean(center.z - depth / 2) }
}

/**
 * Tourne la boîte autour de son centre pour que sa poignée (devant, −Z local) pointe vers `pointer`.
 * Angle arrondi à `stepDeg` degrés, ramené dans [0, 360[.
 */
export function rotateBoxToward(g: GeometryBox, pointer: { x: number; z: number }, stepDeg = 15): GeometryBox {
  const c = geometryCenter(g)
  const dx = pointer.x - c.x
  const dz = pointer.z - c.z
  if (Math.hypot(dx, dz) < 1e-6) return g
  // Poignée en local (0, −r) → monde (−r·sin a, −r·cos a) : a = atan2(−dx, −dz).
  const deg = round(Math.atan2(-dx, -dz) / DEG, stepDeg)
  return { ...g, rotationY: clean(((deg % 360) + 360) % 360) }
}

/**
 * Calque plan photo d'un lieu (place) : rectangle au sol où la photo est projetée.
 * Mis à l'échelle en traçant un segment de longueur connue (mètres).
 */
export const PlanOverlaySchema = z.object({
  version: z.literal(GEOMETRY_VERSION),
  kind: z.literal('plan_overlay'),
  x: z.number().finite(),
  z: z.number().finite(),
  width: z.number().positive().max(500),
  depth: z.number().positive().max(500),
  rotationY: z.number().finite().default(0),
  locked: z.boolean().default(false),
})
export type PlanOverlay = z.infer<typeof PlanOverlaySchema>

export function parsePlanOverlay(raw: unknown): PlanOverlay | null {
  const parsed = PlanOverlaySchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}

/** Rectangle plan par défaut (10 × 8 m) avant calibration. */
export function defaultPlanOverlay(): PlanOverlay {
  return {
    version: GEOMETRY_VERSION,
    kind: 'plan_overlay',
    x: 0,
    z: 0,
    width: 10,
    depth: 8,
    rotationY: 0,
    locked: false,
  }
}

/**
 * Recale le plan pour qu'un segment tracé (en mètres courants) vaille `realMeters`.
 * Homothétie centrée sur le milieu du segment.
 */
export function scalePlanOverlay(
  overlay: PlanOverlay,
  segment: { x0: number; z0: number; x1: number; z1: number },
  realMeters: number,
): PlanOverlay {
  const dx = segment.x1 - segment.x0
  const dz = segment.z1 - segment.z0
  const current = Math.hypot(dx, dz)
  if (!(realMeters > 0) || !(current > 1e-6)) return overlay
  const factor = realMeters / current
  const midX = (segment.x0 + segment.x1) / 2
  const midZ = (segment.z0 + segment.z1) / 2
  return {
    ...overlay,
    x: midX + (overlay.x - midX) * factor,
    z: midZ + (overlay.z - midZ) * factor,
    width: Math.min(500, Math.max(0.5, overlay.width * factor)),
    depth: Math.min(500, Math.max(0.5, overlay.depth * factor)),
  }
}
